/* eslint-disable */
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { RefreshCw, Check, X, Clock, Database, TrendingUp, ChevronDown, ChevronUp, Star } from 'lucide-react';

const C = {
  cream:'#F7F6EF', charcoal:'#2F3A42', navy:'#445366', mid:'#7A8A96', border:'#D8DEE3',
  skyBlue:'#BCD9E5', skyBlueLight:'#EAF4F8', skyText:'#3A8FA3',
  green:'#7EA594', greenLight:'#EAF2EF',
  amber:'#E07B00', amberLight:'#FFF3E0',
  red:'#DC3545', redLight:'#FFF0F0',
  white:'#FFFFFF',
};

const BASE_URL = 'https://open-api.eitje.app/open_api';

const EITJE_ENV_MAP = {
  3080: 'Box Jordaan',
  3081: 'Box Plantage',
  5537: 'Sassys',
  3660: 'Prep Kitchen',
};

const VENUES = [
  { id: 3080, name: 'Box Jordaan'  },
  { id: 3081, name: 'Box Plantage' },
  { id: 5537, name: 'Sassys'       },
  { id: 3660, name: 'Prep Kitchen' },
];

const TYPE_MAP = {
  gewerkte_uren:    'gewerkte_uren',
  verlof:           'verlof',
  ziek:             'ziek',
  bijzonder_verlof: 'bijzonder_verlof',
};

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }

// Returns { monday, sunday } as Date objects for a given ISO week + year
function isoWeekBounds(week, year) {
  const jan4 = new Date(year, 0, 4);
  const startW1 = new Date(jan4);
  startW1.setDate(jan4.getDate() - (jan4.getDay() + 6) % 7);
  const monday = new Date(startW1);
  monday.setDate(startW1.getDate() + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { monday, sunday };
}

// How many ISO weeks does a year have?
function isoWeeksInYear(year) {
  const dec28 = new Date(year, 11, 28);
  dec28.setDate(dec28.getDate() + 3 - (dec28.getDay() + 6) % 7);
  const week1 = new Date(dec28.getFullYear(), 0, 4);
  return 1 + Math.round(((dec28 - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
}

// Revenue group ID → venue mapping (Eitje returns all groups on every API call)
const REVENUE_GROUP_VENUE = {
  3985: 'Box Jordaan',
  3986: 'Box Plantage',
  6775: 'Sassys',
};

// Teams classified as Kitchen in Lemon Tree
const KITCHEN_TEAMS = ['kitchen', 'kp', 'prep kitchen'];
// Teams excluded from all hour counts (duplicates / non-operational)
const IGNORED_TEAMS = ['algemeen'];

function isKitchenTeam(teamName) {
  if (!teamName) return false;
  return KITCHEN_TEAMS.some(k => teamName.toLowerCase().includes(k));
}
function isIgnoredTeam(teamName) {
  if (!teamName) return false;
  return IGNORED_TEAMS.some(k => teamName.toLowerCase() === k);
}

// After a weekly sync, re-aggregate the full month containing this week
// and upsert kitchen_hours + foh_hours into results_monthly.
// This keeps results_monthly current without any manual upload step.
// Module-level (not inside a component) so both HoursSync and BulkHoursSync can call it.
async function writeMonthHoursToResultsMonthly(wkStart) {
  // Determine which month(s) this week touches (a week can straddle two months)
  const monday = new Date(wkStart);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  const months = [{ year: monday.getFullYear(), month: monday.getMonth() + 1 }];
  if (sunday.getMonth() !== monday.getMonth()) {
    months.push({ year: sunday.getFullYear(), month: sunday.getMonth() + 1 });
  }

  for (const { year: y, month: mo } of months) {
    const monthStart = `${y}-${String(mo).padStart(2,'0')}-01`;
    const lastDay    = new Date(y, mo, 0).getDate();
    const monthEnd   = `${y}-${String(mo).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`;

    const { data: shifts } = await supabase
      .from('eitje_hours')
      .select('venue, team, hours_worked')
      .eq('type', 'gewerkte_uren')
      .gte('date', monthStart)
      .lte('date', monthEnd);

    if (!shifts?.length) continue;

    const map = {};
    shifts.forEach(s => {
      if (!s.venue || isIgnoredTeam(s.team)) return;
      if (s.type !== 'gewerkte_uren') return;
      if (!map[s.venue]) map[s.venue] = { kitchen: 0, foh: 0 };
      const hrs = parseFloat(s.hours_worked) || 0;
      if (isKitchenTeam(s.team)) map[s.venue].kitchen += hrs;
      else                       map[s.venue].foh     += hrs;
    });

    for (const [venue, hrs] of Object.entries(map)) {
      const kitchen = Math.round(hrs.kitchen * 10) / 10;
      const foh     = Math.round(hrs.foh     * 10) / 10;

      const { error: monthlyError } = await supabase.from('results_monthly')
        .upsert(
          { venue, year: y, month: mo, kitchen_hours: kitchen, foh_hours: foh },
          { onConflict: 'venue,year,month', ignoreDuplicates: false }
        );

      if (monthlyError) {
        console.error('results_monthly upsert failed:', venue, y, mo, monthlyError);
      }
    }
  }
}

function getWeekNumber(dateStr) {
  const d = new Date(dateStr);
  d.setHours(0,0,0,0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const week1 = new Date(d.getFullYear(), 0, 4);
  return {
    week: 1 + Math.round(((d - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7),
    year: d.getFullYear(),
  };
}

function card(accent) {
  return {
    background: C.white, borderRadius:'12px', border:`1px solid ${C.border}`,
    padding:'20px 24px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)',
    borderLeft: accent ? `4px solid ${accent}` : undefined,
  };
}

const label = { fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'5px' };
const input = { width:'100%', padding:'8px 10px', border:`1.5px solid ${C.border}`, borderRadius:'8px', fontSize:'13px', boxSizing:'border-box', background:C.white, color:C.charcoal, outline:'none', fontFamily:'inherit' };

function btn(color, disabled) {
  return {
    padding:'8px 18px', borderRadius:'8px', border:'none',
    background: disabled ? C.border : (color || C.charcoal),
    color: C.white, fontWeight:700, fontSize:'13px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1, transition:'opacity 0.15s',
    display:'flex', alignItems:'center', gap:'6px',
  };
}

function StatusBadge({ status }) {
  const map = {
    success: { bg:C.greenLight,  color:C.green, icon:'✓', label:'Success' },
    error:   { bg:C.redLight,    color:C.red,   icon:'✗', label:'Error'   },
    running: { bg:C.amberLight,  color:C.amber, icon:'…', label:'Running' },
  };
  const s = map[status];
  if (!s) return null;
  return (
    <span style={{ padding:'3px 10px', borderRadius:'20px', background:s.bg, color:s.color, fontSize:'12px', fontWeight:700 }}>
      {s.icon} {s.label}
    </span>
  );
}

function ProgressBar({ current, total }) {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <div style={{ marginTop:'8px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', color:C.mid, marginBottom:'4px' }}>
        <span>Chunk {current} of {total}</span>
        <span>{pct}%</span>
      </div>
      <div style={{ height:'6px', background:C.cream, borderRadius:'3px', overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background:C.navy, borderRadius:'3px', transition:'width 0.3s' }}/>
      </div>
    </div>
  );
}

// ── Hours Sync Section ───────────────────────────────────────────────────────
function HoursSync({ creds, onLog }) {
  const today = new Date();
  const currentYear = today.getFullYear();

  // Compute current ISO week number
  function currentISOWeek() {
    const d = new Date(today);
    d.setHours(0,0,0,0);
    d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
    const week1 = new Date(d.getFullYear(), 0, 4);
    return 1 + Math.round(((d - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
  }

  const [selectedWeek, setSelectedWeek] = useState(currentISOWeek());
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [running,      setRunning]      = useState(false);
  const [result,       setResult]       = useState(null);
  const [lastSync,     setLastSync]     = useState(null);
  const [auditRows,    setAuditRows]    = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const totalWeeks = isoWeeksInYear(selectedYear);
  const weekOptions = Array.from({ length: totalWeeks }, (_, i) => i + 1);
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  const { monday, sunday } = isoWeekBounds(selectedWeek, selectedYear);
  const weekStart = isoDate(monday);
  const weekEnd   = isoDate(sunday);

  useEffect(() => {
    async function loadLastSync() {
      const { data } = await supabase
        .from('eitje_sync_log')
        .select('*')
        .eq('resource', 'hours')
        .eq('status', 'success')
        .order('completed_at', { ascending: false })
        .limit(1);
      if (data?.[0]) setLastSync(data[0]);
    }
    loadLastSync();
  }, [result]);

  // Load audit table from eitje_hours for selected week
  async function loadAudit() {
    setAuditLoading(true);
    const { data, error } = await supabase
      .from('eitje_hours')
      .select('date, user_name, venue, team, type, hours_worked, approved')
      .gte('date', weekStart)
      .lte('date', weekEnd)
      .order('date')
      .order('venue');
    setAuditRows(error ? [] : (data || []));
    setAuditLoading(false);
  }

  // Aggregate gewerkte_uren for a complete Mon–Sun week → write to results
  async function writeWeekHoursToResults(week, year, wkStart, wkEnd) {
    const { data: shifts, error } = await supabase
      .from('eitje_hours')
      .select('venue, team, hours_worked, type')
      .eq('type', 'gewerkte_uren')
      .gte('date', wkStart)
      .lte('date', wkEnd);

    if (error || !shifts) return;

    // Aggregate by venue
    const map = {};
    shifts.forEach(s => {
      if (!s.venue) return;
      if (isIgnoredTeam(s.team)) return;
      if (s.type !== 'gewerkte_uren') return;
      if (!map[s.venue]) map[s.venue] = { kitchen: 0, foh: 0 };
      const hrs = parseFloat(s.hours_worked) || 0;
      if (isKitchenTeam(s.team)) map[s.venue].kitchen += hrs;
      else                       map[s.venue].foh     += hrs;
    });

    // Upsert each venue's row in results
    for (const [venue, hrs] of Object.entries(map)) {
      const kitchen = Math.round(hrs.kitchen * 10) / 10;
      const foh     = Math.round(hrs.foh     * 10) / 10;
      const total   = Math.round((hrs.kitchen + hrs.foh) * 10) / 10;

      const { data: existing } = await supabase
        .from('results')
        .select('id')
        .eq('venue', venue)
        .eq('year',  year)
        .eq('week',  week)
        .maybeSingle();

      if (existing?.id) {
        await supabase.from('results')
          .update({ kitchen_hours: kitchen, foh_hours: foh, total_hours: total })
          .eq('id', existing.id);
      } else {
        await supabase.from('results').insert({
          venue, year, week,
          kitchen_hours: kitchen, foh_hours: foh, total_hours: total,
          brunch_revenue: 0, dinner_revenue: 0,
        });
      }
    }
  }

  function getHeaders() {
    return {
      'Content-Type':     'application/json',
      'Partner-Username': creds.eitje_partner_username,
      'Partner-Password': creds.eitje_partner_password,
      'Api-Username':     creds.eitje_api_username,
      'Api-Password':     creds.eitje_api_password,
    };
  }

  async function runSync() {
    setRunning(true);
    setResult(null);
    setAuditRows(null);

    const logId = crypto.randomUUID();

    await supabase.from('eitje_sync_log').insert({
      id: logId, resource:'hours', trigger_type:'manual',
      start_date: weekStart, end_date: weekEnd, status:'running',
    });

    let totalSynced = 0;
    let errorMsg    = null;

    try {
      const url = `${BASE_URL}/time_registration_shifts?filters[start_date]=${weekStart}&filters[end_date]=${weekEnd}`;
      const res  = await fetch(url, { method:'GET', headers: getHeaders() });
      const text = await res.text();
      if (!text) throw new Error('Empty response from Eitje');
      const json = JSON.parse(text);
      if (!res.ok) throw new Error(json?.message || json?.error || `${res.status}`);

      const allShifts = json.items || [];

      // Deduplicate by id
      const seen = new Set();
      const deduped = allShifts.filter(s => { if (seen.has(s.id)) return false; seen.add(s.id); return true; });

      const rows = deduped.map(s => ({
        id:             s.id,
        date:           s.date,
        start_time:     s.start,
        end_time:       s.end,
        break_minutes:  s.break_minutes || 0,
        eitje_user_id:  s.user?.id,
        user_name:      s.user?.name,
        environment_id: s.environment?.id,
        venue:          EITJE_ENV_MAP[s.environment?.id] || s.environment?.name,
        team:           s.team?.name,
        type:           s.type?.name || null,
        approved:       s.approved || false,
        synced_at:      new Date().toISOString(),
      }));

      const BATCH = 200;
      for (let i = 0; i < rows.length; i += BATCH) {
        const batch = rows.slice(i, i + BATCH);
        const { error } = await supabase
          .from('eitje_hours')
          .upsert(batch, { onConflict: 'id' });
        if (error) throw new Error(error.message);
        totalSynced += batch.length;
      }

      // Write-back: aggregate the synced week's hours to results table
      // (Mon–Sun is always a complete week, so no partial-week problem)
      await writeWeekHoursToResults(selectedWeek, selectedYear, weekStart, weekEnd);

      // Also update results_monthly for any month(s) this week touches
      await writeMonthHoursToResultsMonthly(weekStart);

      await supabase.from('eitje_sync_log')
        .update({ status:'success', records_synced: totalSynced, completed_at: new Date().toISOString() })
        .eq('id', logId);

      setResult({ status:'success', records: totalSynced });
      onLog();

      // Auto-load audit after sync
      await loadAudit();

    } catch(e) {
      errorMsg = e.message;
      await supabase.from('eitje_sync_log')
        .update({ status:'error', error_msg: errorMsg, completed_at: new Date().toISOString() })
        .eq('id', logId);
      setResult({ status:'error', message: errorMsg });
    } finally {
      setRunning(false);
    }
  }

  // Summarise audit rows by venue + team type
  const auditSummary = auditRows ? (() => {
    const map = {};
    auditRows.filter(r => r.type === 'gewerkte_uren' && !isIgnoredTeam(r.team)).forEach(r => {
      const key = r.venue;
      if (!map[key]) map[key] = { kitchen: 0, foh: 0 };
      const hrs = parseFloat(r.hours_worked) || 0;
      if (isKitchenTeam(r.team)) map[key].kitchen += hrs;
      else map[key].foh += hrs;
    });
    return map;
  })() : null;

  return (
    <div style={card(C.navy)}>
      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'16px' }}>
        <Clock size={16} color={C.navy}/>
        <span style={{ fontWeight:800, fontSize:'15px', color:C.charcoal, flex:1 }}>Hours Sync</span>
        {lastSync && (
          <span style={{ fontSize:'11px', color:C.mid }}>
            Last synced: {new Date(lastSync.completed_at).toLocaleString('en-GB',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})} · {lastSync.records_synced} records
          </span>
        )}
      </div>

      <div style={{ fontSize:'12px', color:C.mid, marginBottom:'14px' }}>
        Fetches all shift types for one exact ISO week (Mon–Sun). Upserts into <code style={{background:C.cream,padding:'1px 5px',borderRadius:'4px'}}>eitje_hours</code> and writes aggregated kitchen/FOH hours to <code style={{background:C.cream,padding:'1px 5px',borderRadius:'4px'}}>results</code>.
      </div>

      {/* Week + Year picker */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'10px' }}>
        <div>
          <span style={label}>Week</span>
          <select value={selectedWeek} onChange={e => setSelectedWeek(Number(e.target.value))} style={input}>
            {weekOptions.map(w => <option key={w} value={w}>W{w}</option>)}
          </select>
        </div>
        <div>
          <span style={label}>Year</span>
          <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))} style={input}>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <div style={{ fontSize:'12px', color:C.mid, marginBottom:'14px' }}>
        {weekStart} → {weekEnd}
      </div>

      {running && <ProgressBar current={1} total={1}/>}

      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'14px' }}>
        <button onClick={runSync} disabled={running} style={btn(C.navy, running)}>
          <RefreshCw size={13} style={running?{animation:'spin 1s linear infinite'}:{}}/> {running ? 'Syncing…' : 'Sync W' + selectedWeek}
        </button>
        <button onClick={loadAudit} disabled={auditLoading} style={btn(C.mid, auditLoading)}>
          {auditLoading ? 'Loading…' : 'Audit W' + selectedWeek}
        </button>
        {result && (
          result.status === 'success'
            ? <span style={{ fontSize:'13px', color:C.green, fontWeight:600 }}>✓ {result.records} records synced</span>
            : <span style={{ fontSize:'13px', color:C.red, fontWeight:600 }}>✗ {result.message}</span>
        )}
      </div>

      {/* Summary totals */}
      {auditSummary && Object.keys(auditSummary).length > 0 && (
        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'12px' }}>
          {Object.entries(auditSummary).map(([venue, hrs]) => (
            <div key={venue} style={{ background:C.skyBlueLight, border:`1px solid ${C.skyBlue}`, borderRadius:'8px', padding:'8px 14px', fontSize:'12px' }}>
              <span style={{ fontWeight:700, color:C.charcoal }}>{venue}</span>
              <span style={{ color:C.mid, marginLeft:'8px' }}>K: <strong>{Math.round(hrs.kitchen * 10)/10}h</strong></span>
              <span style={{ color:C.mid, marginLeft:'6px' }}>FOH: <strong>{Math.round(hrs.foh * 10)/10}h</strong></span>
              <span style={{ color:C.navy, marginLeft:'6px' }}>Total: <strong>{Math.round((hrs.kitchen+hrs.foh)*10)/10}h</strong></span>
            </div>
          ))}
        </div>
      )}

      {/* Audit table */}
      {auditRows && (
        <div style={{ overflowX:'auto', borderRadius:'8px', border:`1px solid ${C.border}`, marginTop:'4px' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
            <thead>
              <tr style={{ background:C.cream, borderBottom:`1px solid ${C.border}` }}>
                {['Date','Name','Venue','Team','Type','Hours','Approved'].map(h => (
                  <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontWeight:700, color:C.mid, fontSize:'11px', textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {auditRows.length === 0
                ? <tr><td colSpan={7} style={{ padding:'16px', textAlign:'center', color:C.mid }}>No records found for this week</td></tr>
                : auditRows.map((r, i) => (
                  <tr key={i} style={{ borderBottom:`1px solid ${C.border}`, background: i%2===0 ? C.white : C.cream }}>
                    <td style={{ padding:'6px 10px', color:C.charcoal }}>{r.date}</td>
                    <td style={{ padding:'6px 10px', color:C.charcoal, fontWeight:500 }}>{r.user_name || '—'}</td>
                    <td style={{ padding:'6px 10px', color:C.mid }}>{r.venue || '—'}</td>
                    <td style={{ padding:'6px 10px', color:C.mid }}>{r.team || '—'}</td>
                    <td style={{ padding:'6px 10px' }}>
                      <span style={{ padding:'2px 8px', borderRadius:'12px', fontSize:'11px', fontWeight:600,
                        background: r.type==='gewerkte_uren' ? C.greenLight : C.amberLight,
                        color: r.type==='gewerkte_uren' ? C.green : C.amber }}>
                        {r.type || '—'}
                      </span>
                    </td>
                    <td style={{ padding:'6px 10px', fontWeight:700, color:C.charcoal }}>
                      {r.hours_worked ? (Math.round(parseFloat(r.hours_worked)*100)/100)+'h' : '—'}
                    </td>
                    <td style={{ padding:'6px 10px', color: r.approved ? C.green : C.mid }}>
                      {r.approved ? '✓' : '—'}
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Bulk Hours Sync ──────────────────────────────────────────────────────────
// Iterates week-by-week through a date range, calling the Eitje API for each
// week and upserting into eitje_hours. Used for backfilling historical data.
function BulkHoursSync({ creds, onLog }) {
  const today = new Date();
  const currentYear = today.getFullYear();

  // Default range: 12 weeks ago through last week
  const defaultEnd = new Date(today);
  defaultEnd.setDate(defaultEnd.getDate() - ((defaultEnd.getDay() + 6) % 7) - 1); // last Sunday
  const defaultStart = new Date(defaultEnd);
  defaultStart.setDate(defaultStart.getDate() - 12*7 + 1); // 12 weeks before that
  // Snap to monday
  defaultStart.setDate(defaultStart.getDate() - ((defaultStart.getDay() + 6) % 7));

  const [startDate, setStartDate] = useState(isoDate(defaultStart));
  const [endDate,   setEndDate]   = useState(isoDate(defaultEnd));
  const [running,   setRunning]   = useState(false);
  const [progress,  setProgress]  = useState({ current: 0, total: 0, week: '' });
  const [results,   setResults]   = useState(null); // { ok: [], failed: [], totalRecords: 0 }
  const [confirmed, setConfirmed] = useState(false);

  function getHeaders() {
    return {
      'Content-Type':     'application/json',
      'Partner-Username': creds.eitje_partner_username,
      'Partner-Password': creds.eitje_partner_password,
      'Api-Username':     creds.eitje_api_username,
      'Api-Password':     creds.eitje_api_password,
    };
  }

  // Build list of Mon–Sun weeks covering the date range (inclusive)
  function buildWeekList(startIso, endIso) {
    const start = new Date(startIso);
    const end   = new Date(endIso);
    // Snap start back to its Monday
    const monday = new Date(start);
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    const weeks = [];
    let cursor = new Date(monday);
    while (cursor <= end) {
      const wkStart = new Date(cursor);
      const wkEnd   = new Date(cursor);
      wkEnd.setDate(wkEnd.getDate() + 6);
      const { week, year } = getWeekNumber(isoDate(wkStart));
      weeks.push({
        week, year,
        weekStart: isoDate(wkStart),
        weekEnd:   isoDate(wkEnd),
      });
      cursor.setDate(cursor.getDate() + 7);
    }
    return weeks;
  }

  // Single-week sync — reuses the same upsert+results-writeback logic as HoursSync
  async function syncOneWeek({ week, year, weekStart, weekEnd }) {
    const logId = crypto.randomUUID();
    await supabase.from('eitje_sync_log').insert({
      id: logId, resource:'hours', trigger_type:'bulk',
      start_date: weekStart, end_date: weekEnd, status:'running',
    });

    try {
      const url = `${BASE_URL}/time_registration_shifts?filters[start_date]=${weekStart}&filters[end_date]=${weekEnd}`;
      const res  = await fetch(url, { method:'GET', headers: getHeaders() });
      const text = await res.text();
      if (!text) throw new Error('Empty response from Eitje');
      const json = JSON.parse(text);
      if (!res.ok) throw new Error(json?.message || json?.error || `${res.status}`);

      const allShifts = json.items || [];
      const seen = new Set();
      const deduped = allShifts.filter(s => { if (seen.has(s.id)) return false; seen.add(s.id); return true; });

      const rows = deduped.map(s => ({
        id:             s.id,
        date:           s.date,
        start_time:     s.start,
        end_time:       s.end,
        break_minutes:  s.break_minutes || 0,
        eitje_user_id:  s.user?.id,
        user_name:      s.user?.name,
        environment_id: s.environment?.id,
        venue:          EITJE_ENV_MAP[s.environment?.id] || s.environment?.name,
        team:           s.team?.name,
        type:           s.type?.name || null,
        approved:       s.approved || false,
        synced_at:      new Date().toISOString(),
      }));

      let inserted = 0;
      const BATCH = 200;
      for (let i = 0; i < rows.length; i += BATCH) {
        const batch = rows.slice(i, i + BATCH);
        const { error } = await supabase.from('eitje_hours').upsert(batch, { onConflict: 'id' });
        if (error) throw new Error(error.message);
        inserted += batch.length;
      }

      // Aggregate to results table for this week
      const { data: shifts } = await supabase
        .from('eitje_hours')
        .select('venue, team, hours_worked, type')
        .eq('type', 'gewerkte_uren')
        .gte('date', weekStart)
        .lte('date', weekEnd);

      if (shifts) {
        const map = {};
        shifts.forEach(s => {
          if (!s.venue) return;
          if (isIgnoredTeam(s.team)) return;
          if (!map[s.venue]) map[s.venue] = { kitchen: 0, foh: 0 };
          const hrs = parseFloat(s.hours_worked) || 0;
          if (isKitchenTeam(s.team)) map[s.venue].kitchen += hrs;
          else                       map[s.venue].foh     += hrs;
        });
        for (const [venue, hrs] of Object.entries(map)) {
          const kitchen = Math.round(hrs.kitchen * 10) / 10;
          const foh     = Math.round(hrs.foh     * 10) / 10;
          const total   = Math.round((hrs.kitchen + hrs.foh) * 10) / 10;
          const { data: existing } = await supabase
            .from('results').select('id')
            .eq('venue', venue).eq('year', year).eq('week', week)
            .maybeSingle();
          if (existing?.id) {
            await supabase.from('results')
              .update({ kitchen_hours: kitchen, foh_hours: foh, total_hours: total })
              .eq('id', existing.id);
          } else {
            await supabase.from('results').insert({
              venue, year, week,
              kitchen_hours: kitchen, foh_hours: foh, total_hours: total,
              brunch_revenue: 0, dinner_revenue: 0,
            });
          }
        }
      }

      // Also update results_monthly for any month(s) this week touches
      // (previously only happened via the single-week manual sync — this was the gap
      // causing results_monthly to go stale whenever weeks were synced via bulk/backfill)
      await writeMonthHoursToResultsMonthly(weekStart);

      await supabase.from('eitje_sync_log')
        .update({ status:'success', records_synced: inserted, completed_at: new Date().toISOString() })
        .eq('id', logId);

      return { ok: true, records: inserted };
    } catch (e) {
      await supabase.from('eitje_sync_log')
        .update({ status:'error', error_msg: e.message, completed_at: new Date().toISOString() })
        .eq('id', logId);
      return { ok: false, error: e.message };
    }
  }

  async function runBulk() {
    if (!confirmed) return;
    setRunning(true);
    setResults(null);
    const weeks = buildWeekList(startDate, endDate);
    const ok = [], failed = [];
    let totalRecords = 0;

    for (let i = 0; i < weeks.length; i++) {
      const w = weeks[i];
      setProgress({ current: i, total: weeks.length, week: `${w.year} W${w.week} (${w.weekStart})` });
      const r = await syncOneWeek(w);
      if (r.ok) { ok.push({ ...w, records: r.records }); totalRecords += r.records; }
      else      { failed.push({ ...w, error: r.error }); }
      // Politeness delay so we don't hammer Eitje
      await new Promise(res => setTimeout(res, 500));
    }

    setProgress({ current: weeks.length, total: weeks.length, week: 'Done' });
    setResults({ ok, failed, totalRecords });
    setRunning(false);
    setConfirmed(false);
    if (onLog) onLog();
  }

  const weeks = (startDate && endDate && startDate <= endDate) ? buildWeekList(startDate, endDate) : [];
  const estSeconds = weeks.length * 2.5; // ~2.5s per week including 500ms delay

  return (
    <div style={card(C.amber)}>
      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'16px' }}>
        <RefreshCw size={16} color={C.amber}/>
        <span style={{ fontWeight:800, fontSize:'15px', color:C.charcoal, flex:1 }}>Bulk Hours Sync</span>
        <span style={{ padding:'3px 10px', borderRadius:'20px', background:C.amberLight, color:C.amber, fontSize:'11px', fontWeight:700 }}>BACKFILL</span>
      </div>

      <div style={{ fontSize:'12px', color:C.mid, marginBottom:'14px', lineHeight:1.5 }}>
        Loops through every Mon–Sun week in the chosen range, syncing one week per Eitje API call (Eitje limits range to ~7 days per call). Continues on individual week failures. Use this to backfill historical Eitje data.
      </div>

      {/* Date range pickers */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'10px' }}>
        <div>
          <span style={label}>Start Date</span>
          <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setConfirmed(false); }} style={input}/>
        </div>
        <div>
          <span style={label}>End Date</span>
          <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setConfirmed(false); }} style={input}/>
        </div>
      </div>

      <div style={{ fontSize:'12px', color:C.mid, marginBottom:'14px' }}>
        {weeks.length > 0 ? (
          <>
            <strong style={{ color:C.charcoal }}>{weeks.length}</strong> weeks to sync · estimated <strong style={{ color:C.charcoal }}>~{Math.ceil(estSeconds)}s</strong> total
          </>
        ) : (
          <span style={{ color:C.red }}>Invalid date range</span>
        )}
      </div>

      {running && (
        <div style={{ marginBottom:'10px' }}>
          <ProgressBar current={progress.current} total={progress.total}/>
          <div style={{ fontSize:'11px', color:C.mid, marginTop:'4px' }}>
            Syncing: <strong style={{ color:C.charcoal }}>{progress.week}</strong>
          </div>
        </div>
      )}

      {/* Confirm checkbox + run button */}
      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'10px', flexWrap:'wrap' }}>
        <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', color:C.charcoal, cursor:'pointer' }}>
          <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} disabled={running || weeks.length === 0}/>
          I want to sync {weeks.length} week{weeks.length === 1 ? '' : 's'}
        </label>
        <button onClick={runBulk} disabled={running || !confirmed || weeks.length === 0} style={btn(C.amber, running || !confirmed || weeks.length === 0)}>
          <RefreshCw size={13} style={running?{animation:'spin 1s linear infinite'}:{}}/> {running ? 'Bulk Syncing…' : 'Start Bulk Sync'}
        </button>
      </div>

      {/* Results */}
      {results && (
        <div style={{ marginTop:'14px', borderRadius:'8px', border:`1px solid ${C.border}`, overflow:'hidden' }}>
          <div style={{ background: results.failed.length === 0 ? C.greenLight : C.amberLight, padding:'10px 14px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontWeight:700, fontSize:'13px', color: results.failed.length === 0 ? C.green : C.amber }}>
              {results.failed.length === 0 ? '✓ All weeks synced' : `⚠ ${results.failed.length} of ${results.ok.length + results.failed.length} weeks failed`}
            </span>
            <span style={{ fontSize:'12px', color:C.mid }}>
              {results.totalRecords.toLocaleString()} total records inserted
            </span>
          </div>
          {results.failed.length > 0 && (
            <div style={{ padding:'10px 14px', maxHeight:'160px', overflowY:'auto' }}>
              <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', marginBottom:'6px' }}>Failed weeks</div>
              {results.failed.map((f, i) => (
                <div key={i} style={{ fontSize:'12px', padding:'4px 0', borderBottom: i < results.failed.length-1 ? `1px solid ${C.border}` : 'none' }}>
                  <strong style={{ color:C.red }}>{f.year} W{f.week}</strong>
                  <span style={{ color:C.mid, marginLeft:'8px' }}>{f.weekStart}</span>
                  <span style={{ color:C.red, marginLeft:'10px', fontSize:'11px' }}>{f.error}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Revenue Sync Section ─────────────────────────────────────────────────────
function RevenueSync({ creds, onLog }) {
  const today = new Date();
  const currentYear = today.getFullYear();

  function currentISOWeek() {
    const d = new Date(today);
    d.setHours(0,0,0,0);
    d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
    const week1 = new Date(d.getFullYear(), 0, 4);
    return 1 + Math.round(((d - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
  }

  const [mode,         setMode]         = useState('week'); // 'week' | 'today'
  const [selectedWeek, setSelectedWeek] = useState(currentISOWeek());
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [running,      setRunning]      = useState(false);
  const [progress,     setProgress]     = useState({ current:0, total:0 });
  const [result,       setResult]       = useState(null);
  const [lastSync,     setLastSync]     = useState(null);

  const totalWeeks  = isoWeeksInYear(selectedYear);
  const weekOptions = Array.from({ length: totalWeeks }, (_, i) => i + 1);
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  const { monday, sunday } = isoWeekBounds(selectedWeek, selectedYear);
  const weekStart = isoDate(monday);
  const weekEnd   = isoDate(sunday);
  const todayStr  = isoDate(today);

  // Resolve actual date range to sync
  const syncStart = mode === 'week' ? weekStart : todayStr;
  const syncEnd   = mode === 'week' ? weekEnd   : todayStr;

  useEffect(() => {
    async function loadLastSync() {
      const { data } = await supabase
        .from('eitje_sync_log')
        .select('*')
        .eq('resource', 'revenue')
        .eq('status', 'success')
        .order('completed_at', { ascending: false })
        .limit(1);
      if (data?.[0]) setLastSync(data[0]);
    }
    loadLastSync();
  }, [result]);

  function getHeaders() {
    return {
      'Content-Type':     'application/json',
      'Partner-Username': creds.eitje_partner_username,
      'Partner-Password': creds.eitje_partner_password,
      'Api-Username':     creds.eitje_api_username,
      'Api-Password':     creds.eitje_api_password,
    };
  }

  // Aggregate Totaal group from eitje_revenue rows → upsert results_daily
  async function writeRevenueToResultsDaily(revenueRows) {
    const map = {};
    revenueRows.forEach(r => {
      const venue = REVENUE_GROUP_VENUE[r.revenue_group_id];
      if (!venue) return; // skip unknown/sub groups
      const key = `${venue}|${r.date}`;
      if (!map[key]) map[key] = { venue, date: r.date, cents: 0 };
      map[key].cents += r.amt_in_cents || 0;
    });

    const dailyRows = Object.values(map).map(r => {
      const d = new Date(r.date + 'T00:00:00');
      const { week, year } = getWeekNumber(r.date);
      return {
        venue:         r.venue,
        date:          r.date,
        year:          year,
        week:          week,
        month:         d.getMonth() + 1,
        total_revenue: Math.round(r.cents / 100),
      };
    });

    if (!dailyRows.length) return;

    const BATCH = 200;
    for (let i = 0; i < dailyRows.length; i += BATCH) {
      const batch = dailyRows.slice(i, i + BATCH);
      const { error } = await supabase
        .from('results_daily')
        .upsert(batch, { onConflict: 'venue,date' });
      if (error) console.error('[Revenue write-back] upsert error:', error);
    }
  }

  async function runSync() {
    setRunning(true);
    setResult(null);

    const logId = crypto.randomUUID();

    // Build list of dates to sync (timezone-safe — use noon to avoid DST edge cases)
    const dates = [];
    let cur = new Date(syncStart + 'T12:00:00');
    const end = new Date(syncEnd + 'T12:00:00');
    while (cur <= end) { dates.push(isoDate(cur)); cur = addDays(cur, 1); }

    const totalCalls = dates.length * VENUES.length;
    setProgress({ current:0, total:totalCalls });

    await supabase.from('eitje_sync_log').insert({
      id: logId, resource:'revenue', trigger_type:'manual',
      start_date: syncStart, end_date: syncEnd, status:'running',
    });

    let totalSynced = 0;
    let callsDone   = 0;
    let errorMsg    = null;
    const allRows   = [];

    try {
      for (const date of dates) {
        for (const venue of VENUES) {
          callsDone++;
          setProgress({ current:callsDone, total:totalCalls });

          const url = `${BASE_URL}/revenue_days?environment_id=${venue.id}&filters[start_date]=${date}&filters[end_date]=${date}`;
          try {
            const res  = await fetch(url, { method:'GET', headers: getHeaders() });
            const text = await res.text();
            if (!text || !res.ok) continue;
            const json = JSON.parse(text);
            (json.items || []).forEach(item => {
              allRows.push({
                id:                 item.id,
                date:               item.date || date,
                environment_id:     venue.id,
                venue:              venue.name,
                revenue_group_id:   item.revenue_group?.id,
                revenue_group_name: item.revenue_group?.name,
                amt_in_cents:       item.amt_in_cents || 0,
                synced_at:          new Date().toISOString(),
              });
            });
          } catch {}
        }
      }

      // Deduplicate by id
      const seenRev = new Set();
      const dedupedRows = allRows.filter(r => {
        if (seenRev.has(r.id)) return false;
        seenRev.add(r.id);
        return true;
      });

      const BATCH = 200;
      for (let i = 0; i < dedupedRows.length; i += BATCH) {
        const batch = dedupedRows.slice(i, i + BATCH);
        const { error } = await supabase
          .from('eitje_revenue')
          .upsert(batch, { onConflict: 'id' });
        if (error) throw new Error(error.message);
        totalSynced += batch.length;
      }

      // Write-back: aggregate Totaal revenue into results_daily
      await writeRevenueToResultsDaily(dedupedRows);

      await supabase.from('eitje_sync_log')
        .update({ status:'success', records_synced: totalSynced, completed_at: new Date().toISOString() })
        .eq('id', logId);

      setResult({ status:'success', records: totalSynced });
      onLog();

    } catch(e) {
      errorMsg = e.message;
      await supabase.from('eitje_sync_log')
        .update({ status:'error', error_msg: errorMsg, completed_at: new Date().toISOString() })
        .eq('id', logId);
      setResult({ status:'error', message: errorMsg });
    } finally {
      setRunning(false);
      setProgress({ current:0, total:0 });
    }
  }

  return (
    <div style={card(C.green)}>
      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'16px' }}>
        <TrendingUp size={16} color={C.green}/>
        <span style={{ fontWeight:800, fontSize:'15px', color:C.charcoal, flex:1 }}>Revenue Sync</span>
        {lastSync && (
          <span style={{ fontSize:'11px', color:C.mid }}>
            Last synced: {new Date(lastSync.completed_at).toLocaleString('en-GB',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})} · {lastSync.records_synced} records
          </span>
        )}
      </div>

      <div style={{ fontSize:'12px', color:C.mid, marginBottom:'14px' }}>
        Fetches daily revenue from Eitje for all venues. Upserts into <code style={{background:C.cream,padding:'1px 5px',borderRadius:'4px'}}>eitje_revenue</code> and writes aggregated revenue to <code style={{background:C.cream,padding:'1px 5px',borderRadius:'4px'}}>results_daily</code>.
      </div>

      {/* Mode toggle: Week or Today */}
      <div style={{ display:'flex', gap:'6px', marginBottom:'14px' }}>
        {[['week','By Week'],['today','Today']].map(([k,l]) => (
          <button key={k} onClick={() => setMode(k)} style={{
            padding:'4px 14px', borderRadius:'20px', border:`1px solid ${mode===k ? C.green : C.border}`,
            background: mode===k ? C.greenLight : C.cream,
            color: mode===k ? C.green : C.mid,
            fontSize:'12px', fontWeight:600, cursor:'pointer',
          }}>{l}</button>
        ))}
      </div>

      {mode === 'week' ? (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'10px' }}>
            <div>
              <span style={label}>Week</span>
              <select value={selectedWeek} onChange={e => setSelectedWeek(Number(e.target.value))} style={input}>
                {weekOptions.map(w => <option key={w} value={w}>W{w}</option>)}
              </select>
            </div>
            <div>
              <span style={label}>Year</span>
              <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))} style={input}>
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <div style={{ fontSize:'12px', color:C.mid, marginBottom:'14px' }}>{weekStart} → {weekEnd}</div>
        </>
      ) : (
        <div style={{ fontSize:'12px', color:C.mid, marginBottom:'14px' }}>
          Syncing today only: <strong style={{ color:C.charcoal }}>{todayStr}</strong>
        </div>
      )}

      {running && <ProgressBar current={progress.current} total={progress.total}/>}

      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginTop:'4px' }}>
        <button onClick={runSync} disabled={running} style={btn(C.green, running)}>
          <RefreshCw size={13} style={running?{animation:'spin 1s linear infinite'}:{}}/> {running ? 'Syncing…' : mode === 'week' ? 'Sync W' + selectedWeek : 'Sync Today'}
        </button>
        {result && (
          result.status === 'success'
            ? <span style={{ fontSize:'13px', color:C.green, fontWeight:600 }}>✓ {result.records} records synced</span>
            : <span style={{ fontSize:'13px', color:C.red, fontWeight:600 }}>✗ {result.message}</span>
        )}
      </div>
    </div>
  );
}

// ── Xero Contacts Sync ────────────────────────────────────────────────────────
function XeroContactsSync({ onLog }) {
  const [running,  setRunning]  = useState(false);
  const [result,   setResult]   = useState(null);
  const [count,    setCount]    = useState(null);
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => { loadStatus(); }, [result]);

  async function loadStatus() {
    const [logRes, cacheRes] = await Promise.all([
      supabase.from('eitje_sync_log').select('*')
        .eq('resource', 'xero_contacts').eq('status', 'success')
        .order('completed_at', { ascending: false }).limit(1),
      supabase.from('xero_contacts_cache').select('contact_id', { count: 'exact', head: true }),
    ]);
    if (logRes.data?.[0]) setLastSync(logRes.data[0]);
    setCount(cacheRes.count || 0);
  }

  async function runSync() {
    setRunning(true);
    setResult(null);
    try {
      const url = `${SUPABASE_FUNCTIONS_URL}/xero-sync-contacts`;
      const res  = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-trigger': 'manual' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setResult({ status: 'success', synced: data.synced });
    } catch (e) {
      setResult({ status: 'error', message: e.message });
    } finally {
      setRunning(false);
      if (onLog) onLog();
    }
  }

  return (
    <div style={card(C.skyText)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <TrendingUp size={16} color={C.skyText} />
        <span style={{ fontWeight: 800, fontSize: '15px', color: C.charcoal, flex: 1 }}>Xero Contacts Sync</span>
        <span style={{ padding: '3px 10px', borderRadius: '20px', background: C.skyBlueLight, color: C.skyText, fontSize: '11px', fontWeight: 700 }}>XERO</span>
      </div>

      <div style={{ fontSize: '12px', color: C.mid, marginBottom: '14px', lineHeight: 1.5 }}>
        Fetches all supplier contacts from Xero and updates the contacts cache used by the Finance tab supplier dropdown.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
        <div style={{ background: C.cream, borderRadius: '8px', padding: '10px 14px', border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Contacts Cached</div>
          <div style={{ fontSize: '22px', fontWeight: 900, color: C.skyText }}>{count ?? '—'}</div>
        </div>
        <div style={{ background: C.cream, borderRadius: '8px', padding: '10px 14px', border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Last Sync</div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: C.charcoal, marginTop: '4px' }}>
            {lastSync ? new Date(lastSync.completed_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Never'}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <button onClick={runSync} disabled={running} style={btn(C.skyText, running)}>
          <RefreshCw size={13} style={running ? { animation: 'spin 1s linear infinite' } : {}} />
          {running ? 'Syncing…' : 'Sync Contacts'}
        </button>
        {result && (
          result.status === 'success'
            ? <span style={{ fontSize: '13px', color: C.green, fontWeight: 600 }}>✓ {result.synced} contacts synced</span>
            : <span style={{ fontSize: '13px', color: C.red, fontWeight: 600 }}>✗ {result.message}</span>
        )}
      </div>
    </div>
  );
}

// ── Google Reviews Sync ───────────────────────────────────────────────────────
const SUPABASE_FUNCTIONS_URL = 'https://unvzfprqkmxrifwodlec.supabase.co/functions/v1';

function GoogleReviewsSync({ onLog }) {
  const [running,  setRunning]  = useState(false);
  const [result,   setResult]   = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [count,    setCount]    = useState(null);

  useEffect(() => { loadStatus(); }, [result]);

  async function loadStatus() {
    const [countRes, latestRes] = await Promise.all([
      supabase.from('google_reviews').select('id', { count: 'exact', head: true }),
      supabase.from('google_reviews').select('synced_at').order('synced_at', { ascending: false }).limit(1),
    ]);
    setCount(countRes.count ?? 0);
    if (latestRes.data?.[0]) setLastSync(latestRes.data[0].synced_at);
  }

  async function runSync() {
    setRunning(true);
    setResult(null);
    try {
      const url = `${SUPABASE_FUNCTIONS_URL}/sync-google-reviews-nightly`;
      const res  = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-trigger': 'manual' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setResult({ status: 'success', synced: data.synced ?? data.total ?? '?' });
    } catch (e) {
      setResult({ status: 'error', message: e.message });
    } finally {
      setRunning(false);
      if (onLog) onLog();
    }
  }

  return (
    <div style={card(C.amber)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <Star size={16} color={C.amber} fill={C.amber} strokeWidth={1.5} />
        <span style={{ fontWeight: 800, fontSize: '15px', color: C.charcoal, flex: 1 }}>Google Reviews Sync</span>
        <span style={{ padding: '3px 10px', borderRadius: '20px', background: C.amberLight, color: C.amber, fontSize: '11px', fontWeight: 700 }}>GOOGLE</span>
      </div>

      <div style={{ fontSize: '12px', color: C.mid, marginBottom: '14px', lineHeight: 1.5 }}>
        Fetches reviews from the last 48 hours across all venues via the Google Business Profile API.
        Runs automatically nightly — use this button to trigger a manual refresh.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
        <div style={{ background: C.cream, borderRadius: '8px', padding: '10px 14px', border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Reviews Cached</div>
          <div style={{ fontSize: '22px', fontWeight: 900, color: C.amber }}>{count ?? '—'}</div>
        </div>
        <div style={{ background: C.cream, borderRadius: '8px', padding: '10px 14px', border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Last Sync</div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: C.charcoal, marginTop: '4px' }}>
            {lastSync ? new Date(lastSync).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Never'}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <button onClick={runSync} disabled={running} style={btn(C.amber, running)}>
          <RefreshCw size={13} style={running ? { animation: 'spin 1s linear infinite' } : {}} />
          {running ? 'Syncing…' : 'Sync Now (48h window)'}
        </button>
        {result && (
          result.status === 'success'
            ? <span style={{ fontSize: '13px', color: C.green, fontWeight: 600 }}>✓ {result.synced} reviews synced</span>
            : <span style={{ fontSize: '13px', color: C.red, fontWeight: 600 }}>✗ {result.message}</span>
        )}
      </div>
    </div>
  );
}

// ── Xero P&L Sync ────────────────────────────────────────────────────────────

function XeroPLSync({ onLog }) {
  const [running,  setRunning]  = useState(false);
  const [result,   setResult]   = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [cached,   setCached]   = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  useEffect(() => { loadStatus(); }, [result]);

  async function loadStatus() {
    const [logRes, cacheRes] = await Promise.all([
      supabase.from('eitje_sync_log').select('*')
        .eq('resource', 'xero_pl').eq('status', 'success')
        .order('completed_at', { ascending: false }).limit(1),
      supabase.from('xero_pl_cache').select('period_key', { count: 'exact' }),
    ]);
    if (logRes.data?.[0]) setLastSync(logRes.data[0]);
    setCached(cacheRes.count || 0);
  }

  async function runSync(single = null) {
    setRunning(true);
    setResult(null);
    setProgress({ current: 0, total: single ? 1 : 18 });

    try {
      const url = `${SUPABASE_FUNCTIONS_URL}/xero-sync-pl${single ? `?month=${single}` : ''}`;
      const res  = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-trigger': 'manual' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setProgress({ current: data.synced || 0, total: data.total || 0 });
      setResult({ status: 'success', synced: data.synced, total: data.total, errors: data.errors });
    } catch (e) {
      setResult({ status: 'error', message: e.message });
    } finally {
      setRunning(false);
      if (onLog) onLog();
    }
  }

  // Month picker for single-month refresh
  const now = new Date();
  const monthOptions = [];
  for (let i = 0; i < 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    monthOptions.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  const [singleMonth, setSingleMonth] = useState(monthOptions[0]);

  return (
    <div style={card(C.skyText)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <TrendingUp size={16} color={C.skyText} />
        <span style={{ fontWeight: 800, fontSize: '15px', color: C.charcoal, flex: 1 }}>Xero P&L Sync</span>
        <span style={{ padding: '3px 10px', borderRadius: '20px', background: C.skyBlueLight, color: C.skyText, fontSize: '11px', fontWeight: 700 }}>XERO</span>
      </div>

      <div style={{ fontSize: '12px', color: C.mid, marginBottom: '14px', lineHeight: 1.5 }}>
        Fetches monthly P&L reports from Xero (Jan 2025 → now) and caches them in Supabase.
        The Finance tab reads from this cache — no live Xero calls needed. Runs automatically nightly at 2am.
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
        <div style={{ background: C.cream, borderRadius: '8px', padding: '10px 14px', border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Months Cached</div>
          <div style={{ fontSize: '22px', fontWeight: 900, color: C.skyText }}>{cached ?? '—'}</div>
        </div>
        <div style={{ background: C.cream, borderRadius: '8px', padding: '10px 14px', border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Last Sync</div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: C.charcoal, marginTop: '4px' }}>
            {lastSync ? new Date(lastSync.completed_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Never'}
          </div>
        </div>
      </div>

      {running && <ProgressBar current={progress.current} total={progress.total || 18} />}

      {/* Full sync */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <button onClick={() => runSync()} disabled={running} style={btn(C.skyText, running)}>
          <RefreshCw size={13} style={running ? { animation: 'spin 1s linear infinite' } : {}} />
          {running ? 'Syncing…' : 'Sync All Months'}
        </button>
        {result && (
          result.status === 'success'
            ? <span style={{ fontSize: '13px', color: C.green, fontWeight: 600 }}>✓ {result.synced} of {result.total} months synced{result.errors?.length > 0 ? ` (${result.errors.length} errors)` : ''}</span>
            : <span style={{ fontSize: '13px', color: C.red, fontWeight: 600 }}>✗ {result.message}</span>
        )}
      </div>

      {/* Single month refresh */}
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '14px' }}>
        <span style={label}>Refresh Single Month</span>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select value={singleMonth} onChange={e => setSingleMonth(e.target.value)} style={{ ...input, width: 'auto', minWidth: 140 }}>
            {monthOptions.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <button onClick={() => runSync(singleMonth)} disabled={running} style={btn(C.mid, running)}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sync Log ─────────────────────────────────────────────────────────────────
function SyncLog({ refresh }) {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadLogs(); }, [refresh]);

  async function loadLogs() {
    setLoading(true);
    const { data } = await supabase
      .from('eitje_sync_log')
      .select('*')
      .order('started_at', { ascending: false })
      .limit(50);
    setLogs(data || []);
    setLoading(false);
  }

  // Table stats
  const [stats, setStats] = useState({});
  useEffect(() => {
    async function loadStats() {
      const [h, r] = await Promise.all([
        supabase.from('eitje_hours').select('id', { count:'exact', head:true }),
        supabase.from('eitje_revenue').select('id', { count:'exact', head:true }),
      ]);
      setStats({ hours: h.count || 0, revenue: r.count || 0 });
    }
    loadStats();
  }, [refresh]);

  return (
    <div style={card()}>
      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'16px' }}>
        <Database size={16} color={C.mid}/>
        <span style={{ fontWeight:800, fontSize:'15px', color:C.charcoal, flex:1 }}>Sync Log & Table Stats</span>
      </div>

      {/* Table stats */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'20px' }}>
        {[
          { label:'eitje_hours records',   value:stats.hours,   color:C.navy  },
          { label:'eitje_revenue records', value:stats.revenue, color:C.green },
        ].map(s => (
          <div key={s.label} style={{ background:C.cream, borderRadius:'10px', padding:'14px 16px', border:`1px solid ${C.border}` }}>
            <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'4px' }}>{s.label}</div>
            <div style={{ fontSize:'22px', fontWeight:900, color:s.color }}>{(s.value||0).toLocaleString()}</div>
          </div>
        ))}
      </div>

      {/* Log table */}
      {loading ? (
        <div style={{ padding:'20px', textAlign:'center', color:C.mid, fontSize:'13px' }}>Loading…</div>
      ) : logs.length === 0 ? (
        <div style={{ padding:'20px', textAlign:'center', color:C.mid, fontSize:'13px' }}>No sync runs yet</div>
      ) : (
        <div style={{ overflowX:'auto', borderRadius:'8px', border:`1px solid ${C.border}` }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
            <thead>
              <tr style={{ background:C.cream, borderBottom:`1px solid ${C.border}` }}>
                {['Resource','Trigger','Date Range','Status','Records','Started','Duration'].map(h => (
                  <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:700, color:C.mid, fontSize:'11px', textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => {
                const dur = log.completed_at
                  ? Math.round((new Date(log.completed_at) - new Date(log.started_at)) / 1000)
                  : null;
                return (
                  <tr key={log.id} style={{ borderBottom:`1px solid ${C.border}`, background: i%2===0 ? C.white : C.cream }}>
                    <td style={{ padding:'8px 12px', fontWeight:600, color:C.charcoal, textTransform:'capitalize' }}>{log.resource}</td>
                    <td style={{ padding:'8px 12px', color:C.mid }}>{log.trigger_type?.replace('_',' ')}</td>
                    <td style={{ padding:'8px 12px', color:C.mid, whiteSpace:'nowrap' }}>
                      {log.start_date} → {log.end_date}
                    </td>
                    <td style={{ padding:'8px 12px' }}><StatusBadge status={log.status}/></td>
                    <td style={{ padding:'8px 12px', color:C.charcoal, fontWeight:600 }}>{log.records_synced ?? '—'}</td>
                    <td style={{ padding:'8px 12px', color:C.mid, whiteSpace:'nowrap' }}>
                      {new Date(log.started_at).toLocaleString('en-GB',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}
                    </td>
                    <td style={{ padding:'8px 12px', color:C.mid }}>{dur != null ? `${dur}s` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════
export default function DataSync({ currentUser }) {
  const [creds,      setCreds]      = useState(null);
  const [credsError, setCredsError] = useState(null);
  const [logRefresh, setLogRefresh] = useState(0);

  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    async function loadCreds() {
      const { data, error } = await supabase
        .from('app_settings')
        .select('key, value')
        .in('key', ['eitje_partner_username','eitje_partner_password','eitje_api_username','eitje_api_password']);
      if (error || !data || data.length < 4) {
        setCredsError('Eitje credentials not found in app_settings. Run the setup SQL to add them.');
        return;
      }
      const c = {};
      data.forEach(r => { c[r.key] = r.value; });
      setCreds(c);
    }
    loadCreds();
  }, []);

  if (!isAdmin) return (
    <div style={{ padding:'3rem', textAlign:'center', color:C.mid }}>
      <div style={{ fontSize:'32px', marginBottom:'12px' }}>🔒</div>
      <div style={{ fontSize:'16px', fontWeight:700, color:C.charcoal }}>Admin only.</div>
    </div>
  );

  return (
    <div style={{ padding:'24px', maxWidth:'900px', fontFamily:'inherit' }}>
      {/* Header */}
      <div style={{ marginBottom:'24px', paddingBottom:'16px', borderBottom:`2px solid ${C.border}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'4px' }}>
          <h2 style={{ margin:0, fontSize:'22px', fontWeight:900, color:C.charcoal }}>Data Sync</h2>
          <span style={{ padding:'3px 10px', borderRadius:'20px', background:C.amberLight, color:C.amber, fontSize:'11px', fontWeight:700 }}>ADMIN</span>
        </div>
        <p style={{ margin:0, fontSize:'13px', color:C.mid }}>
          Manually trigger Eitje data pulls and monitor sync history. Automatic scheduling via Supabase Edge Functions coming soon.
        </p>
      </div>

      {credsError && (
        <div style={{ marginBottom:'16px', padding:'14px 16px', background:C.redLight, borderRadius:'10px', border:`1px solid ${C.red}44`, color:C.red, fontSize:'13px' }}>
          <strong>Credentials error:</strong> {credsError}
        </div>
      )}

      {creds && (
        <div style={{ marginBottom:'10px', padding:'10px 14px', background:C.greenLight, borderRadius:'8px', border:`1px solid ${C.green}44`, fontSize:'12px', color:C.green, fontWeight:600 }}>
          ✓ Eitje credentials loaded from app_settings
        </div>
      )}

      <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
        <HoursSync     creds={creds}   onLog={() => setLogRefresh(r => r+1)} />
        <BulkHoursSync creds={creds}   onLog={() => setLogRefresh(r => r+1)} />
        <RevenueSync   creds={creds}   onLog={() => setLogRefresh(r => r+1)} />
        <GoogleReviewsSync             onLog={() => setLogRefresh(r => r+1)} />
        <XeroPLSync                    onLog={() => setLogRefresh(r => r+1)} />
        <XeroContactsSync              onLog={() => setLogRefresh(r => r+1)} />
        <SyncLog       refresh={logRefresh} />
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}